import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Column, Container, Row } from '@pella/sash-framework';
import { Breadcrumbs, CTA, Image, Layout, SEO } from '../../components';
import { isValidArray } from '../../components/utilities/validations';
import { useGlobalConfig } from '../../hooks/use-global-config';

import CTARibbon from '../../components/common/cta-ribbon';
import CardSection from '../../components/common/card-section';
import HeroSimple from '../../components/common/hero-simple';
import InstallInstructions from '../../components/template/instructions/install-instructions';
import ImageClickPoints from '../../components/template/steady-set/image-click-points';
import SteadySetQuote from '../../components/common/forms/steady-set-quote';

const Instructions = (props) => {
	const { instructions_cards } = useGlobalConfig();
	const pageData = props.data.contentstackInstructions;
	const defaultActiveType = pageData.page_components?.find((block) => block.frame_type != null)?.frame_type?.frame_type?.[0];
	const [activeType, setActiveType] = useState({ id: defaultActiveType?.anchor_text, group_heading: defaultActiveType?.group_heading });

	const onFrameTypeClick = (type, heading) => {
		setActiveType({ id: type, group_heading: heading });
		const url = new URL(window.location);
		url.searchParams.set('type', type);
		window.history.pushState({}, '', url);
		document.querySelector('#end-frame-type').scrollIntoView({ behavior: 'smooth' });
	};

	useEffect(() => {
		const anchor = window.location.hash?.substring(1);
		const urlParams = new URLSearchParams(window.location.search);
		const typeParam = urlParams.get('type');
		const targetId = anchor || typeParam;

		const frameTypes = pageData.page_components?.find((block) => block.frame_type != null)?.frame_type?.frame_type || [];

		if (targetId) {
			setActiveType({ id: targetId, group_heading: frameTypes.find((type) => type.anchor_text === targetId)?.group_heading });
			document.querySelector('#end-frame-type').scrollIntoView({ behavior: 'smooth' });
		}
	}, [pageData]);

	return (
		<Layout
			id='template-Instructions'
			hideLeadGen={pageData.page_components?.find((block) => block.lead_gen != null)?.lead_gen ? true : false}
			pageTitle={pageData.seo?.title}
		>
			{pageData.breadcrumbs && (
				<Breadcrumbs
					data={pageData.breadcrumbs}
					currentUrl={pageData.url}
				/>
			)}

			{isValidArray(pageData.hero?.image) && <HeroSimple data={pageData.hero} />}

			{pageData.intro && (
				<Container className='mb-6 md:mb-8 xl:mb-12'>
					<div
						dangerouslySetInnerHTML={{ __html: pageData.intro }}
						className='text-subtitle mx-auto max-w-[80ch] text-pretty text-center'
					/>
				</Container>
			)}

			{pageData.page_components?.[0] &&
				pageData.page_components.map((block, index) => {
					if (isValidArray(block.cta_ribbon?.cta_ribbon)) {
						return (
							<Container
								key={index}
								className='my-6 md:my-8 xl:my-12'
							>
								<CTARibbon data={block.cta_ribbon.cta_ribbon[0]} />
							</Container>
						);
					}

					if (block.cards != null) {
						return (
							<CardSection
								key={index}
								data={block.cards}
							/>
						);
					}

					if (block.frame_type != null) {
						const groupHeading = block.frame_type.frame_type?.find((type) => type.anchor_text === activeType?.id)?.group_heading;

						return (
							<React.Fragment key={index}>
								<div
									id='frame-type'
									className='bg-neutral-50 py-6 md:py-8 xl:py-12'
								>
									<Container>
										{block.frame_type.heading && <h2 className='mb-4 text-center md:mb-6 xl:mb-8'>{block.frame_type.heading}</h2>}

										{isValidArray(block.frame_type.frame_type) && (
											<div className='grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3'>
												{block.frame_type.frame_type.map((type, index) => (
													<div key={index}>
														<button
															className={`block h-full w-full border-4 bg-white p-4 shadow-lg ${activeType.id === type.anchor_text ? 'border-primary' : 'border-transparent'}`}
															onClick={() => onFrameTypeClick(type.anchor_text, type.group_heading)}
														>
															{type.file?.url && <Image data={{ image: type.file }} />}

															{type.heading && <h3 className='mb-1'>{type.heading}</h3>}
															{type.description && <p className='text-body-2 text-pretty'>{type.description}</p>}
														</button>
													</div>
												))}
											</div>
										)}
									</Container>
								</div>

								<div id='end-frame-type'>
									{groupHeading && (
										<Container className='my-6 md:my-8 xl:my-12'>
											<h2 className='text-center'>{groupHeading}</h2>
										</Container>
									)}
								</div>
							</React.Fragment>
						);
					}

					if (block.instruction != null) {
						return (
							<div
								key={index}
								className={`my-6 md:my-8 xl:my-12 ${!activeType?.id || block.instruction.filter?.split(',')?.includes(activeType.id) ? '' : 'hidden'}`}
							>
								<Container>
									<InstallInstructions data={block.instruction} />
								</Container>
							</div>
						);
					}

					if (block.install_type != null) {
						return (
							<div
								key={index}
								className='my-6 md:my-8 xl:my-12'
							>
								<Container>
									{isValidArray(block.install_type?.select) && (
										<Row
											justify='center'
											verticalGutters
										>
											{block.install_type.select.map((type, index) => (
												<Column
													xs={12}
													sm={6}
													md={4}
													xl='fluid'
													key={index}
												>
													<CTA
														data={type.cta?.[0]}
														renderAs='link'
														className='block h-full bg-white no-underline shadow-lg transition-shadow hover:shadow-[0_0_0_6px_#ffde00]'
													>
														{isValidArray(type.image) && (
															<Image
																data={type.image[0]}
																className='!block'
															/>
														)}

														<div className='p-6 text-center'>
															{type.heading && <h3 className='mb-1'>{type.heading}</h3>}
															{type.description && <p className='text-pretty'>{type.description}</p>}
														</div>
													</CTA>
												</Column>
											))}
										</Row>
									)}
								</Container>
							</div>
						);
					}

					if (block.lead_gen != null) {
						return (
							<div
								key={index}
								id={block.lead_gen.anchor_text}
								className={`bg-neutral-25 py-8 md:py-10 xl:py-16 ${!activeType?.id || block.lead_gen.filter?.split(',')?.includes(activeType.id) ? '' : 'hidden'}`}
							>
								<Container>
									<Row
										align='center'
										justify='between'
									>
										<Column
											xs={12}
											md={4}
											xl={5}
										>
											<h2
												className='m-0 text-pretty'
												dangerouslySetInnerHTML={{ __html: block.lead_gen.heading }}
											/>

											{block.lead_gen.subheading && (
												<div
													className='mt-4 md:mt-5 xl:mt-6'
													dangerouslySetInnerHTML={{ __html: block.lead_gen.subheading }}
												/>
											)}
										</Column>

										<Column
											xs={12}
											md={8}
											xl={6}
											className='mt-4 md:mt-0'
										>
											<SteadySetQuote path={pageData.url} />
										</Column>
									</Row>
								</Container>
							</div>
						);
					}

					if (isValidArray(block.popout_image?.popout)) {
						return (
							<div
								key={index}
								className={`py-[42px] md:py-[50px] ${!activeType?.id || block.popout_image.filter?.split(',')?.includes(activeType.id) ? '' : 'hidden'}`}
								style={{ backgroundImage: `url('${block.popout_image.background_file?.url}')` }}
							>
								<Container>
									{block.popout_image.heading && <h2>{block.popout_image.heading}</h2>}

									<ImageClickPoints
										className='mt-[42px] [&>div]:mx-auto [&>div]:w-auto'
										imageClass='!min-h-[343px] [&>div]:h-[inherit] md:!min-h-[440px] xl:!min-h-[600px]'
										data={block.popout_image.popout[0]}
									/>
								</Container>
							</div>
						);
					}

					if (block.config_cards && isValidArray(instructions_cards?.card)) {
						return (
							<CardSection
								key={index}
								data={instructions_cards}
							/>
						);
					}
				})}
		</Layout>
	);
};

Instructions.propTypes = {
	data: PropTypes.object.isRequired,
};

export default Instructions;

// eslint-disable-next-line react/prop-types -- gatsby component
export const Head = ({ data }) => <SEO {...data.contentstackInstructions.seo} />;

export const query = graphql`
	query ($uid: String!) {
		contentstackInstructions(id: { eq: $uid }) {
			url
			breadcrumbs {
				...Breadcrumbs
			}
			hero {
				body
				image {
					...Image
					image {
						url
						gatsbyImageData(quality: 90)
					}
				}
			}
			intro
			page_components {
				cta_ribbon {
					cta_ribbon {
						...CTARibbon
					}
				}
				frame_type {
					background_hex_value
					heading
					frame_type {
						file {
							url
							gatsbyImageData(quality: 90)
						}
						heading
						description
						anchor_text
						group_heading
					}
				}
				instruction {
					anchor_text
					filter
					heading
					description
					demo_assets {
						... on Contentstack_image {
							...Image
							image {
								url
								gatsbyImageData(quality: 90)
							}
						}
						... on Contentstack_video {
							...Video
						}
					}
					table {
						table_heading
						row {
							heading
							english: cta {
								... on Contentstack_cta {
									...CTA
								}
								... on Contentstack_video {
									...Video
								}
							}
							spanish {
								... on Contentstack_cta {
									...CTA
								}
								... on Contentstack_video {
									...Video
								}
							}
							french {
								... on Contentstack_cta {
									...CTA
								}
								... on Contentstack_video {
									...Video
								}
							}
						}
					}
				}
				cards {
					...CardSection
				}
				install_type {
					heading
					select {
						image {
							...Image
							image {
								url
								gatsbyImageData(quality: 90)
							}
						}
						heading
						description
						cta {
							...CTA
						}
					}
				}
				popout_image {
					heading
					filter
					popout {
						...PopoutImage
					}
				}
				lead_gen {
					background_hex_value
					heading
					subheading
					filter
					form {
						heading
						subheading
						success_heading
						description
					}
				}
				config_cards {
					add_config_cards
				}
			}
			seo {
				...SEO
			}
		}
	}
`;
